import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '@/nova/components';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectStudioPeriod, setStudioPeriod } from '@/store/slices/studioPeriod';
import { homePeriodOfReport, reportPeriodFor, type ReportPeriod } from '@/nova/pages/home/shared-period';
import { useAffiliateReports } from '../../hooks/useAffiliateReports';
import {
  monthKey,
  rangeFromPreset,
  rowEntityName,
  metricValue,
  toCsv,
  type ChartMode,
  type DateRange,
  type Dimension,
  type PerfMetricKey,
  type PeriodPreset,
} from '../../reports-helpers';
import { Notice } from '../Notice/Notice';
import { PeriodSelector, type PresetId } from './PeriodSelector';
import { CommissionPanel } from './CommissionPanel';
import { KpiRow } from './KpiRow';
import { TrendChart } from './TrendChart';
import { BreakdownTable } from './BreakdownTable';
import { Dashboard, Toolbar, ToolbarGroup, ToolButton, KpiArea } from './dashboard.styles';

export type ReportsTabProps = {
  /** The active brand's casino-instance id; the dashboard fetches its own report data. */
  casinoInstanceId: string;
  /** Told every period picked here, including one Home cannot show (a single past day), which is not shared. */
  onPeriodPick?: (picked: ReportPeriod) => void;
  /** The period to open on instead of the shared one: the page's latest pick here, so a remount (a tab round-trip,
   *  a brand switch) shows the range the console button opens on. */
  openingPeriod?: ReportPeriod | null;
};

/** Trigger a client-side download of CSV text (real app surface, so a blob download works). */
const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * The Affiliates Reports dashboard: a period selector over KPI tiles (with deltas +
 * sparklines), a Total Commission panel (CPA vs Rev Share split), a metric/by-dimension
 * trend chart, and a dimension breakdown table with CSV export. Owns the period /
 * dimension / chart selection; the data comes from `useAffiliateReports`.
 */
export const ReportsTab = ({ casinoInstanceId, onPeriodPick, openingPeriod }: ReportsTabProps): React.ReactElement => {
  const { t } = useTranslation();

  // Opens on the page's latest pick here, else on the range the owner last picked on any page with one (as closely as
  // this picker can show it), else This month; a pick here becomes that range for every page when Home can show it too.
  const dispatch = useAppDispatch();
  const studioPeriod = useAppSelector(selectStudioPeriod);
  const [opening] = React.useState(
    () => openingPeriod ?? (studioPeriod !== null ? reportPeriodFor(studioPeriod) : null),
  );
  const [preset, setPreset] = React.useState<PeriodPreset>(opening?.preset ?? 'month');
  const [customRange, setCustomRange] = React.useState<DateRange | null>(
    opening?.preset === 'custom' ? opening.range : null,
  );
  const share = React.useCallback(
    (picked: ReportPeriod): void => {
      onPeriodPick?.(picked);
      const shared = homePeriodOfReport(picked);
      if (shared !== null) dispatch(setStudioPeriod(shared));
    },
    [dispatch, onPeriodPick],
  );
  const [dimension, setDimension] = React.useState<Dimension>('affiliate');
  const [chartMode, setChartMode] = React.useState<ChartMode>('metric');
  const [metric, setMetric] = React.useState<PerfMetricKey>('ngr_usd');

  const range = React.useMemo<DateRange>(
    () => (preset === 'custom' && customRange ? customRange : rangeFromPreset(preset === 'custom' ? 'month' : preset, new Date())),
    [preset, customRange],
  );

  const data = useAffiliateReports(casinoInstanceId, range, preset, dimension, chartMode);

  const onPreset = React.useCallback(
    (id: PresetId) => {
      setPreset(id);
      setCustomRange(null);
      share({ preset: id, range: rangeFromPreset(id, new Date()) });
    },
    [share],
  );
  const onCustom = React.useCallback(
    (next: DateRange) => {
      setCustomRange(next);
      setPreset('custom');
      share({ preset: 'custom', range: next });
    },
    [share],
  );

  const handleExport = React.useCallback(() => {
    const showCommission = dimension === 'affiliate';
    const dimHeader = dimension === 'date' ? 'Date' : dimension === 'campaign' ? 'Campaign' : 'Affiliate';
    const headers = [dimHeader, 'Regs', 'FTDs', 'Deposits', 'NGR', ...(showCommission ? ['Commission'] : [])];
    const commById = new Map<string, number | undefined>();
    for (const row of data.commissions?.affiliates ?? []) {
      if (row.affiliate?.id) commById.set(row.affiliate.id, row.commission_usd);
    }
    const rows = data.breakdownRows.map((row) => {
      const m = row.metrics;
      const name = dimension === 'date' ? row.date ?? '' : rowEntityName(row, dimension, 'Unknown');
      const cells: Array<string | number> = [
        name,
        metricValue(m, 'registrations'),
        metricValue(m, 'ftds'),
        metricValue(m, 'deposits_usd'),
        metricValue(m, 'ngr_usd'),
      ];
      if (showCommission) cells.push(row.affiliate?.id ? commById.get(row.affiliate.id) ?? '' : '');
      return cells;
    });
    downloadCsv(`affiliate-report_${dimension}_${range.from}_${range.to}.csv`, toCsv(headers, rows));
  }, [data.breakdownRows, data.commissions, dimension, range.from, range.to]);

  return (
    <Dashboard>
      <Toolbar>
        <ToolbarGroup>
          <PeriodSelector preset={preset} range={range} onPreset={onPreset} onCustom={onCustom} />
        </ToolbarGroup>
        <ToolbarGroup>
          <ToolButton type="button" onClick={data.reload}>
            <Icon name="refresh" size={16} color="currentColor" />
            <Text text={t('Refresh')} tag="span" />
          </ToolButton>
          <ToolButton type="button" onClick={handleExport}>
            <Icon name="download" size={16} color="currentColor" />
            <Text text={t('Export CSV')} tag="span" />
          </ToolButton>
        </ToolbarGroup>
      </Toolbar>

      {data.error ? (
        <Notice
          icon="warning"
          title={t('Could not load reports')}
          body={data.error}
          actionLabel={t('Retry')}
          onAction={data.reload}
        />
      ) : (
        <>
          <KpiArea>
            <CommissionPanel commissions={data.commissions} month={monthKey(range.to)} currency={data.currency} loading={data.loading} />
            <KpiRow
              totals={data.totals}
              prevTotals={data.prevTotals}
              commissions={data.commissions}
              prevCommissions={data.prevCommissions}
              dailyRows={data.dailyRows}
              currency={data.currency}
              loading={data.loading}
            />
          </KpiArea>

          <TrendChart
            dailyRows={data.dailyRows}
            dimensionSeries={data.dimensionSeries}
            dimension={dimension}
            chartMode={chartMode}
            metric={metric}
            currency={data.currency}
            loading={data.loading}
            onChartMode={setChartMode}
            onMetric={setMetric}
          />

          <BreakdownTable
            rows={data.breakdownRows}
            dimension={dimension}
            totals={data.totals}
            commissions={data.commissions}
            currency={data.currency}
            loading={data.loading}
            onDimension={setDimension}
          />
        </>
      )}
    </Dashboard>
  );
};

export default ReportsTab;
